/**
 * Click-tracking utility powered by PostHog (open-source analytics).
 *
 * PostHog automatically assigns each anonymous visitor a `distinct_id`,
 * so pre-login tracking works out of the box. After login, call
 * `setTrackingUser(userId)` to link the anonymous session to the real user.
 *
 * Setup:  sign up at https://posthog.com (free tier = 1 M events / month),
 *         grab your project API key, and paste it into POSTHOG_KEY below.
 */

/// <reference types="vite/client" />
import posthog from "posthog-js";
import { isViewAsOrPending } from "../services/viewAsState";

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY || "";
const POSTHOG_HOST =
  import.meta.env.VITE_POSTHOG_HOST || "https://us.i.posthog.com";

let _initialised = false;

export function initAnalytics() {
  // An admin viewing as a member (services/viewAsState.ts) is not a visit.
  if (_initialised || !POSTHOG_KEY || isViewAsOrPending()) return;
  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    autocapture: false,
    capture_pageview: true,
    persistence: "localStorage+cookie",
  });
  _initialised = true;
}

export function setTrackingUser(
  userId: string | null,
  traits?: Record<string, unknown>,
) {
  if (!_initialised) return;
  if (userId) {
    posthog.identify(userId, traits);
  } else {
    posthog.reset();
  }
}

export function trackClick(
  action: string,
  properties?: Record<string, unknown>,
) {
  if (isViewAsOrPending()) return;          // an admin viewing as a member: not the member's click
  // Mirrored to our own store so a click is answerable without PostHog, and so
  // the two can be compared when their counts diverge. Fire-and-forget, and
  // first: it used to sit behind the PostHog check below, so a build without
  // VITE_POSTHOG_KEY (or a PostHog that threw) lost the site_events copy too.
  try {
    void import("./visitorTracking")
      .then(m => m.trackEvent(action, properties))
      .catch(() => { /* never let analytics break a click */ });
  } catch { /* never let analytics break a click */ }
  if (!_initialised) return;
  posthog.capture("utech_click", { action, ...properties });
}

/**
 * Super-properties: attached by PostHog to every later event from this
 * browser, so any event can be broken down by e.g. the onboarding choice
 * (onb_outcome, onb_industry, onb_keyword, onb_flow). PostHog only; the
 * first-party site_events mirror joins onboarding_progress in SQL instead.
 */
export function registerTrackingProps(props: Record<string, unknown>) {
  if (!_initialised) return;
  try {
    posthog.register(props);
  } catch { /* never let analytics break a click */ }
}
